"use client"

import { useMemo, useState } from "react";
import CryptoJS from "crypto-js";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Clipboard } from "lucide-react";
import { Input } from "@/components/ui/input";
import { calculateFileHashes, copyToClipboard } from "@/lib/utils";
import { useTranslations } from "@/components/i18n/use-translations";

const algorithms = [
    { value: "md5", label: "MD5" },
    { value: "sha1", label: "SHA-1" },
    { value: "sha256", label: "SHA-256" },
    { value: "sha512", label: "SHA-512" },
];

const hashText = (value: string, algorithm: string) => {
    if (!value) return "";

    switch (algorithm) {
        case "md5":
            return CryptoJS.MD5(value).toString(CryptoJS.enc.Hex);
        case "sha1":
            return CryptoJS.SHA1(value).toString(CryptoJS.enc.Hex);
        case "sha256":
            return CryptoJS.SHA256(value).toString(CryptoJS.enc.Hex);
        case "sha512":
            return CryptoJS.SHA512(value).toString(CryptoJS.enc.Hex);
        default:
            return "";
    }
};

export function HashLab() {
    const [algorithm, setAlgorithm] = useState<string>("sha256");
    const [value, setValue] = useState("malware.exe");
    const [file, setFile] = useState<{ name: string; size: number; hashes: { md5: string; sha1: string; sha256: string } | null } | null>(null);
    const { messages } = useTranslations();

    const handleFile = async (selected?: File) => {
        if (!selected) {
            return;
        }
        setFile({ name: selected.name, size: selected.size, hashes: null });
        const hashes = await calculateFileHashes(await selected.arrayBuffer());
        setFile({ name: selected.name, size: selected.size, hashes });
    };

    const digest = useMemo(() => hashText(value, algorithm), [algorithm, value]);

    return (
        <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-8">
            <div className="space-y-5 rounded-2xl border border-slate-800 bg-slate-900/70 p-5 shadow-lg shadow-cyan-500/10">
                <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-cyan-200">{messages.tools.hashLab.label}</p>
                    <h1 className="text-2xl font-semibold text-white">{messages.tools.hashLab.title}</h1>
                    <p className="text-sm text-gray-300">{messages.tools.hashLab.description}</p>
                </div>

                <div className="grid gap-3 sm:grid-cols-[200px_1fr] sm:items-start">
                    <label className="flex flex-col gap-2 text-sm font-medium text-slate-100">
                        {messages.tools.hashLab.label}
                        <select
                            className="w-full rounded-lg border border-slate-700 bg-transparent px-3 py-2 text-sm font-semibold text-slate-100 ring-2 ring-cyan-500/20 focus:border-cyan-400 focus:outline-hidden focus:ring-2 focus:ring-cyan-400/40"
                            value={algorithm}
                            onChange={(event) => setAlgorithm(event.target.value)}
                        >
                            {algorithms.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="flex flex-col gap-2 text-sm font-medium text-slate-100">
                        {messages.tools.hashLab.inputLabel}
                        <Textarea
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                            placeholder={messages.tools.hashLab.placeholder}
                            className="min-h-[140px] border border-slate-800 bg-transparent text-slate-100 placeholder:text-gray-500 ring-2 ring-cyan-500/20 focus:ring-cyan-500/45"
                        />
                    </label>
                </div>
            </div>

            <div className="space-y-3 rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-gray-400">{messages.tools.hashLab.hashLabel}</p>
                        <p className="text-xl font-semibold text-white">
                            {algorithms.find((alg) => alg.value === algorithm)?.label} {messages.tools.defang.outputLabel}
                        </p>
                    </div>
                    <Button
                        type="button"
                        className="h-10 rounded-full bg-linear-to-r from-cyan-500 to-blue-600 px-4 text-sm font-semibold text-white shadow-md shadow-cyan-500/25 transition hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/35"
                        onClick={() => copyToClipboard(digest)}
                    >
                        {messages.tools.hashLab.copy}
                    </Button>
                </div>
                <Textarea
                    value={digest}
                    readOnly
                    aria-label={messages.tools.hashLab.hashLabel}
                    className="min-h-[100px] border border-cyan-800/50 bg-slate-950/60 text-slate-100 ring-2 ring-cyan-500/15 focus-visible:outline-hidden"
                />
                <p className="text-xs text-gray-400">{messages.tools.hashLab.description}</p>
            </div>

            <div className="space-y-3 rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-xl font-semibold text-white">{messages.tools.hashLab.fileTitle}</p>
                        <p className="text-sm text-gray-400">{messages.tools.hashLab.fileDescription}</p>
                    </div>
                    <label className="inline-flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-full bg-linear-to-r from-cyan-500 to-blue-600 px-4 text-sm font-semibold text-white shadow-md shadow-cyan-500/25 transition hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/35">
                        <Input type="file" className="hidden" onChange={(event) => handleFile(event.target.files?.[0] ?? undefined)} />
                        <span>{messages.tools.hashLab.fileButton}</span>
                    </label>
                </div>
                {file && (
                    <div className="space-y-2">
                        <p className="text-xs text-gray-400">
                            <span className="text-gray-200">{file.name}</span> ({file.size.toLocaleString()} bytes)
                        </p>
                        {file.hashes ? (
                            (["md5", "sha1", "sha256"] as const).map((key) => (
                                <div key={key} className="flex items-center justify-between gap-3 rounded-lg border border-slate-800/70 bg-slate-950/60 px-3 py-2">
                                    <div className="min-w-0">
                                        <p className="text-xs uppercase tracking-wider text-gray-400">{key}</p>
                                        <p className="break-all font-mono text-sm text-cyan-100">{file.hashes?.[key]}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => copyToClipboard(file.hashes?.[key] ?? "")}
                                        className="inline-flex shrink-0 items-center justify-center rounded-lg p-2 text-gray-300 transition-colors hover:bg-slate-700/40 hover:text-cyan-400"
                                        aria-label={`${messages.tools.hashLab.copy} ${key}`}
                                    >
                                        <Clipboard className="h-4 w-4" />
                                    </button>
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-gray-400">{messages.tools.hashLab.computing}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
